import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  defaultCountryFieldName,
  FormProps,
  getAddressControlName,
  getRequiredValidationRule,
  getValidationErrorProps,
} from 'util/Form';
import countries from 'store/configs/Countries';
import StaticSelectOption from 'store/types/StaticSelectOption';

interface CountryFormItemProps extends FormProps {
  addressFieldName: string;
  generateControlName?: boolean;
}

const CountryFormItem: React.FunctionComponent<CountryFormItemProps> = ({
  fieldName = defaultCountryFieldName,
  addressFieldName,
  required = true,
  disabled = false,
  generateControlName = true,
}) => {
  const { control } = useFormContext();
  const controlName = useMemo(
    () => (generateControlName ? getAddressControlName(addressFieldName, fieldName) : fieldName),
    [addressFieldName, fieldName, generateControlName]
  );
  const countryValue = useWatch({ control, name: controlName });
  const [selectedValue, setSelectedValue] = useState<StaticSelectOption | null>(null);

  useEffect(() => {
    setSelectedValue(countries.find((state) => state.value === countryValue) || null);
  }, [countryValue]);

  return (
    <Controller
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          blurOnSelect
          autoSelect
          autoHighlight
          disableClearable={selectedValue !== null}
          options={countries}
          getOptionLabel={(option) => option.text || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={selectedValue}
          disabled={disabled}
          onChange={(_event, data) => {
            field.onChange(data?.value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...getValidationErrorProps(error)}
              required={required}
              label={'Country'}
              inputRef={field.ref}
              data-testid={`${fieldName}-text-field`}
              inputProps={{ ...params.inputProps, ['data-testid']: `${fieldName}-input` }}
              FormHelperTextProps={{ ['data-testid']: `${fieldName}-helper-text` } as any}
            />
          )}
        />
      )}
      name={controlName}
      control={control}
      rules={{ required: getRequiredValidationRule('country', true, required) }}
    />
  );
};
export default CountryFormItem;
